import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

// PDF.js, loaded only when sorting mode first shows a CV (CLAUDE.md §5a rule 5 names
// "the PDF viewer" as the kind of chunk to lazy-load; DECISIONS-LOG S15 Q4). The
// documents themselves are cached a few deep, so the next three cards' CVs are
// already parsed when their turn comes (F8.02 "prefetches next 3").

type PdfJs = typeof import('pdfjs-dist');

/** The card on screen, the three behind it, and a few just decided (undo brings them back). */
const CACHE_SIZE = 8;

let library: Promise<PdfJs> | null = null;
interface Entry {
  /** PDF.js's handle on the load — what frees the document's memory and worker. */
  readonly task: Promise<PDFDocumentLoadingTask>;
  readonly promise: Promise<PDFDocumentProxy>;
}

const documents = new Map<string, Entry>();

function pdfjs(): Promise<PdfJs> {
  library ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerSrc = worker.default;
    return lib;
  });
  return library;
}

export function documentUrl(documentId: string): string {
  return `/api/v1/documents/${documentId}`;
}

/** One parsed document per CV, shared by the viewer and the prefetch. */
export function loadPdf(documentId: string): Promise<PDFDocumentProxy> {
  const cached = documents.get(documentId);
  if (cached) {
    // Most recently used goes to the back, so eviction takes the oldest.
    documents.delete(documentId);
    documents.set(documentId, cached);
    return cached.promise;
  }
  const task = pdfjs().then((lib) => lib.getDocument({ url: documentUrl(documentId), withCredentials: true }));
  const entry: Entry = { task, promise: task.then((loading) => loading.promise) };
  // A failed load is not cached: the next attempt (reconnected) tries again.
  entry.promise.catch(() => documents.delete(documentId));
  documents.set(documentId, entry);
  evict();
  return entry.promise;
}

export function prefetchPdf(documentId: string): void {
  if (!documents.has(documentId)) void loadPdf(documentId).catch(() => undefined);
}

function evict(): void {
  while (documents.size > CACHE_SIZE) {
    const oldest = documents.keys().next().value;
    if (oldest === undefined) return;
    const entry = documents.get(oldest);
    documents.delete(oldest);
    void entry?.task.then((loading) => loading.destroy()).catch(() => undefined);
  }
}
